import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { SelectChangeEvent } from "@mui/material";
import {
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { getDocuments } from "../../api/documents";
import { getRequestSteps } from "../../api/processRequest";
import { TextMaskCustom } from "../../components/DocumentTextField";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import Pagination from "../../pages/protected/home/components/Pagination";
import DocumentList from "../../pages/protected/request-history/components/documentList";
import StepsList from "../../pages/protected/request-history/components/stepList";
import Loading from "../../components/Loading";

TextMaskCustom.displayName = "TextMaskCustom";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface DocumentListProp {
  label: string;
  createdAt: string;
  status: string;
  activityName: string;
  id: string;
}

export const Route = createFileRoute("/__auth/request-history/$requestId")({
  component: RequestHistoryPage,
  pendingComponent: Loading,
});

function CustomTabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <div
      aria-labelledby={`simple-tab-${index}`}
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      role="tabpanel"
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

function RequestHistoryPage(): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { title, nup, tenantName } = (location.state as unknown as {
    title: string;
    nup: string;
    tenantName: string;
  }) || { title: "", nup: "", tenantName: "" };
  const [valueTab, setValueTab] = useState(0);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState(0);
  const [documentsList, setDocumentsList] = useState<DocumentListProp[]>([]);
  const [documentLoading, setDocumentsLoading] = useState(false);
  const [documentLoadingError, setDocumentsLoadingError] = useState(false);
  const itemsPerPage = 10;
  const params = Route.useParams();

  const { data: responseProcessSteps, error: processStepsError } = useQuery({
    queryKey: ["request-steps", params.requestId],
    queryFn: async () => await getRequestSteps(params.requestId),
    retry: false,
  });

  useEffect(() => {
    if (processStepsError) {
      navigate({ to: "/error" });
    }
  }, [processStepsError, navigate]);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    setValueTab(newValue);
  };

  useEffect(() => {
    loadingDocuments();
  }, []);

  const loadingDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const response = await getDocuments(params.requestId);
      setDocumentsList(response.data);
    } catch (e) {
      setDocumentsLoadingError(true);
      navigate({ to: "/error" });
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleOrderChange = (event: SelectChangeEvent<number>): void => {
    setOrderBy(event.target.value as number);
  };

  const sortedProcessSteps = (
    responseProcessSteps?.data?.content as any[] | undefined
  )
    ?.slice()
    .sort((a, b) => {
      return orderBy === 0
        ? new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
        : new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
    });

  const paginatedDocumentsList = documentsList.slice(
    page * itemsPerPage,
    (page + 1) * itemsPerPage,
  );

  const paginatedProcessSteps = sortedProcessSteps?.slice(
    page * itemsPerPage,
    (page + 1) * itemsPerPage,
  );

  return (
    <Box className="container">
      <ScrollToTopButton />
      <Stack>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
          }}
        >
          <Link style={{ textDecoration: "none" }} to="/">
            <Typography
              sx={{
                color: "white.light",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                marginRight: "10px",
                textDecoration: "none",
                transition: "text-decoration 0.3s",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              variant="body1"
            >
              {t("requestHistory.breadcrumbRequests")}
            </Typography>
          </Link>
          <ChevronRightIcon fontSize="small" sx={{ color: "white.light" }} />
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
            }}
            textAlign="left"
          >
            {nup}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 500,
          }}
          textAlign="left"
        >
          {title}
        </Typography>
        <Typography color="text.secondary" fontSize={12} fontWeight={400}>
          {tenantName}
        </Typography>
      </Stack>
      <Divider />
      <Stack gap={2}>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: "24px",
            marginRight: "10px",
          }}
          variant="subtitle1"
        >
          {t("requestHistory.historyTitle")}
        </Typography>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs onChange={handleChange} value={valueTab}>
              <Tab id="steps" label={t("requestHistory.stepsTab")} sx={{ width: "50%" }} />
              <Tab id="documents" label={t("requestHistory.documentsTab")} sx={{ width: "50%" }} />
            </Tabs>
          </Box>
          <FormControl
            size="small"
            sx={{
              marginTop: "1.5rem",
              marginBottom: "1.5rem",
              width: "100%",
              "& .MuiFormLabel-root": {
                color: "text.disabled",
              },
              "& .MuiInputBase-input": {
                color: "primary.main",
              },
            }}
          >
            <InputLabel color="primary" id="select-order-by">
              {t("requestHistory.orderByLabel")}
            </InputLabel>
            <Select
              color="primary"
              label={t("requestHistory.orderByLabel")}
              labelId="select-order-by"
              onChange={handleOrderChange}
              value={orderBy}
              variant="outlined"
            >
              <MenuItem key={0} value={0}>
                {t("requestHistory.mostRecent")}
              </MenuItem>
              <MenuItem key={1} value={1}>
                {t("requestHistory.oldest")}
              </MenuItem>
            </Select>
          </FormControl>
          <CustomTabPanel index={0} value={valueTab}>
            {paginatedProcessSteps?.map((step: any) => (
              <StepsList key={step.id} {...(step as any)} />
            ))}
            <div style={{ marginTop: "2rem" }}>
              <Pagination
                itemsPerPage={itemsPerPage}
                page={page}
                setPage={setPage}
                totalItems={
                  (responseProcessSteps?.data?.content as any[])?.length || 0
                }
              />
            </div>
          </CustomTabPanel>
          <CustomTabPanel index={1} value={valueTab}>
            {documentLoading ? (
              <CircularProgress />
            ) : documentLoadingError ? (
              <Typography fontSize={12} fontWeight={400}>
                {t("requestHistory.documentsLoadError")}
              </Typography>
            ) : paginatedDocumentsList.length > 0 ? (
              paginatedDocumentsList.map((document) => (
                <DocumentList key={document.id} {...document} />
              ))
            ) : (
              <Typography fontSize={12} fontWeight={400}>
                {t("requestHistory.noDocuments")}
              </Typography>
            )}
          </CustomTabPanel>
        </Box>
      </Stack>
    </Box>
  );
}
